import {
	DefaultNumberFieldComponent,
	ProgressSpinner,
} from "@constellation-academy/framework-react-components";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useSelector } from "react-redux";
import { readInlineData, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { type DataTableSelectionChangeParams } from "@components/data-table";
import { SearchableTable } from "@components/searchable-table";
import { ValidatedField } from "@components/ValidatedField";
import { getCertificationDataOptions } from "@features/licenses/create-licenses-form/create-licenses-form.utils";
import { useTranslation } from "@i18n/hooks";
import { type createLicensesForm_Query } from "@relay/createLicensesForm_Query.graphql";
import { type createLicensesForm_QueryFragment$key } from "@relay/createLicensesForm_QueryFragment.graphql";
import { type createLicensesForm_Refetch } from "@relay/createLicensesForm_Refetch.graphql";
import {
	type selectLicenseDefinitionField_LicenseDefinitionFragment$data,
	type selectLicenseDefinitionField_LicenseDefinitionFragment$key,
} from "@relay/selectLicenseDefinitionField_LicenseDefinitionFragment.graphql";
import { selectActiveFeatureToggles } from "@store/slices/FeatureTogglesSlice";
import { QUERY, QUERY_FRAGMENT } from "./create-licenses-form.graphql";
import { Submit } from "./create-licenses-form.styles";
import {
	type CreateLicensesFormProps,
	type CreateLicensesFormState,
} from "./create-licenses-form.types";
import { LICENSE_DEFINITION_FRAGMENT } from "../../license-definitions/select-license-definition-field/select-license-definition-field.graphql";

export const CreateLicensesForm = ({ onSubmit }: CreateLicensesFormProps) => {
	const { t } = useTranslation("common");
	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);
	const hasSplitCourseCertification = activeFeatureToggles?.includes("splitCourseCertification");

	const formik = useFormik<CreateLicensesFormState>({
		initialValues: {
			account: undefined,
			amount: 1,
			certificationDataType: "WithCertification",
		},
		enableReinitialize: true,
		onSubmit,
	});

	const query = useLazyLoadQuery<createLicensesForm_Query>(QUERY, { first: 20 });

	const { data, hasPrevious, hasNext, refetch, loadPrevious, loadNext } = usePaginationFragment<
		createLicensesForm_Refetch,
		createLicensesForm_QueryFragment$key
	>(QUERY_FRAGMENT, query);

	const licenseDefinitions = data?.Admin?.LicenseDefinition?.SearchLicenseDefinitions;

	if (!licenseDefinitions) {
		return <ProgressSpinner />;
	}

	const isLicenseSelected = !!formik?.values?.licenseDefinition?.id;

	const clearForm = () => {
		formik.resetForm();
	};

	const handleLoadPrevious = () => {
		loadPrevious(20);
	};

	const handleLoadNext = () => {
		loadNext(20);
	};

	const handleOnSearch = (search: string) => {
		refetch({ nameMatchRegex: search || null });
	};

	const handleOnSelectionChange = (
		event: DataTableSelectionChangeParams<selectLicenseDefinitionField_LicenseDefinitionFragment$data>,
	) => {
		formik.setFieldValue("licenseDefinition", event.value);
	};

	const licenseDefinitionValues =
		licenseDefinitions?.edges
			?.filter((e) => !!e?.node)
			.map((e) =>
				readInlineData<selectLicenseDefinitionField_LicenseDefinitionFragment$key>(
					LICENSE_DEFINITION_FRAGMENT,
					e!.node,
				),
			) || [];

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<div className="flex flex-column ">
				<ValidatedField<CreateLicensesFormState, number>
					name="amount"
					label={t("license.amount")}
					component={DefaultNumberFieldComponent}
					formikConfig={formik}
				/>

				<div className="field">
					<label htmlFor="licenseDefinition">{t("license.licenseDefinition")}</label>
					{formik.values.licenseDefinition ? (
						<div className="flex align-items-center mb-2">
							<div className="flex-1 flex bg-gray-100 border-gray-300 border-1 border-round px-3 py-2">
								{formik.values.licenseDefinition.data.name}
							</div>
							<Button
								type="button"
								className="p-button p-component p-button-secondary ml-2"
								onClick={() => formik.setFieldValue("licenseDefinition", undefined)}
								icon="pi pi-times"
							/>
						</div>
					) : (
						<div className="mb-2">
							<SearchableTable<selectLicenseDefinitionField_LicenseDefinitionFragment$data>
								selectionMode="single"
								selection={formik.values.licenseDefinition}
								dataKey="id"
								value={licenseDefinitionValues}
								hasPrevious={hasPrevious}
								hasNext={hasNext}
								loadPrevious={handleLoadPrevious}
								loadNext={handleLoadNext}
								onSearch={handleOnSearch}
								onSelectionChange={handleOnSelectionChange}
							>
								{(Column) => (
									<>
										<Column header={t("table.name")} field="data.name" />
									</>
								)}
							</SearchableTable>
						</div>
					)}
				</div>

				{isLicenseSelected && hasSplitCourseCertification && (
					<div className="field">
						<label htmlFor="certificationDataType">{t("license.licenseType")}</label>
						<Dropdown
							id="certificationDataType"
							value={formik.values.certificationDataType}
							options={getCertificationDataOptions(t)}
							onChange={(e) => formik.setFieldValue("certificationDataType", e.value)}
							placeholder={t("license.selectLicenseType")}
						/>
					</div>
				)}

				<div>
					<Submit disabled={false} type="submit" icon="pi pi-save" />
					<Button disabled={false} type="reset" onClick={clearForm} icon="pi pi-times" />
				</div>
			</div>
		</form>
	);
};
